// This component is an attempt to build a compound component so we can compose it however we like. It is future proof in order to easily extend and make different variations of the product card
// Usage by composition:
/*
    import ProductCard from "web/react/components/product-card/react";
    ...
    <ProductCard product={product_data}>
        {any ProductCard element or custom element that we want}

        <ProductCard.Image width={<number>} height={<number>}>
            {any ProductCard element or custom element that we want}
        </ProductCard.Image>

        {any ProductCard element or custom element that we want}

        <ProductCard.Details />

        {any ProductCard element or custom element that we want}
    </ProductCard>

    Additionally every component has access to the product card data. The `useProductCardContext` hook can be imported and then used to have access to the data and avoid passing props to the components.

    Check storybook for examples.
*/

import clsx from "clsx";
import React, { RefObject } from "react";
import { ProductCardSerializer } from "web/types/serializers";
import ProductCardBadges from "./product-card-badges";
import { ProductCardDetails } from "./product-card-details";
import ProductCardImage from "./product-card-image";
import ProductCardSaveForLaterButton from "./product-card-save-for-later";
import styles from "./product-card.module.css";

export interface ProductCardProps {
    product: ProductCardSerializer;
    children: React.ReactNode;
    className?: string;
    dataTestId?: string;
    productCardRef?: RefObject<HTMLDivElement> | null;
}

const ProductCardContext = React.createContext<ProductCardSerializer | undefined>(undefined);

export function useProductCardContext(): ProductCardSerializer {
    const context = React.useContext(ProductCardContext);

    if (!context) {
        throw new Error(`useProductCard must be used within a ProductCardProvider`);
    }

    return context;
}

function ProductCard({
    product,
    className,
    dataTestId,
    children,
    productCardRef,
}: ProductCardProps): React.ReactElement {
    return (
        <ProductCardContext.Provider value={product}>
            <div
                ref={productCardRef}
                id={product.slug}
                className={clsx(styles.productCard, className)}
                data-testid={dataTestId}
            >
                {children}
            </div>
        </ProductCardContext.Provider>
    );
}

// LEGACY, please use components inside /emo/ subfolder
// when using the ProductCard component from now on
ProductCard.Badges = ProductCardBadges;
ProductCard.Image = ProductCardImage;
ProductCard.SaveForLaterButton = ProductCardSaveForLaterButton;
ProductCard.Details = ProductCardDetails;

export default ProductCard;
